'use client';
import React, { useEffect, useRef, useState } from 'react';

interface CountdownTimerProps {
  beginTime: number; // 活动开始时间戳（毫秒）
  endTime: number; // 活动结束时间戳（毫秒）
  currentTime: number;
  className?: string;
}

/**
 * 精准倒计时组件（参考timer.tsx实现）
 * 只显示距离endTime的倒计时，自动校准服务端时间
 */
const CountdownTimer: React.FC<CountdownTimerProps> = ({
  beginTime,
  endTime,
  className = '',
  currentTime,
}) => {
  const [countdown, setCountdown] = useState<string[] | null>(null);
  // 不再做服务器时间校准，直接用本地时间
  const initialTimeRef = useRef(Date.now());

  useEffect(() => {
    // 只在组件挂载时初始化 currentTime 与本地时间的差值，后续不再变化
    const refreshCountdown = () => {
      // 使用传入的currentTime加上已过时间差
      const now = currentTime + (Date.now() - initialTimeRef.current);

      // 只有当前时间大于等于 beginTime 并且小于 endTime 时，才进行倒计时
      if (now < beginTime) {
        // 活动未开始，全部显示为 00:00:00
        setCountdown(null);
        return;
      }

      // 参数校验，确保时间戳为有效数字
      if (isNaN(beginTime) || isNaN(endTime) || endTime <= beginTime) {
        setCountdown(null);
        return;
      }

      let duration = Math.max(Math.floor((endTime - now) / 1000), 0);

      // 活动已结束，全部显示为 00:00:00
      if (duration <= 0) {
        setCountdown(null);
        return;
      }

      // 防止 NaN
      if (isNaN(duration)) {
        setCountdown(null);
        return;
      }
      const h = Math.floor(duration / 3600);
      const m = Math.floor((duration % 3600) / 60);
      const s = duration % 60;
      setCountdown([
        String(h).padStart(2, '0'),
        String(m).padStart(2, '0'),
        String(s).padStart(2, '0'),
      ]);
    };

    refreshCountdown(); // 初始化立即刷新

    const timer = setInterval(() => {
      refreshCountdown();
    }, 1000);

    return () => clearInterval(timer);
  }, [beginTime, endTime]);

  return countdown ? (
    <span className={className}>
      {countdown[1]}:{countdown[2]}
    </span>
  ) : null;
};

export default CountdownTimer;
